import { MIME_TYPES } from 'shared/constants/MIME_TYPES';
import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';
import { isBlobUrl } from './blobUrl';

const PLACEHOLDER_SOURCE = '#';
const PDF_RESPONSE_CONTENT_TYPE_PARAM =
  `response-content-type=${encodeURIComponent(MIME_TYPES.PDF)}`;
const JPEG_RESPONSE_CONTENT_TYPE_PARAM =
  `response-content-type=${encodeURIComponent(MIME_TYPES.IMAGE_JPEG)}`;
const PNG_RESPONSE_CONTENT_TYPE_PARAM =
  `response-content-type=${encodeURIComponent(MIME_TYPES.IMAGE_PNG)}`;
const IMAGE_EXTENSION_PATTERN = /\.(webp|jpe?g|png|gif|bmp|svg)(\?|#|$)/i;

type KnownSignedSourceMimeType =
  | typeof MIME_TYPES.PDF
  | typeof MIME_TYPES.IMAGE_JPEG
  | typeof MIME_TYPES.IMAGE_PNG
  | typeof MIME_TYPES.OCTET_STREAM;

/**
 * Returns true when URL explicitly encodes PDF content type in signed URL params.
 */
export function isPdfSignedSource(url: string): boolean {
  return url.includes(PDF_RESPONSE_CONTENT_TYPE_PARAM);
}

/**
 * Infers MIME type from signed URL response-content-type params.
 */
export function getMimeTypeFromSignedSourceUrl(
  url: string
): KnownSignedSourceMimeType {
  if (isPdfSignedSource(url)) {
    return MIME_TYPES.PDF;
  }
  if (url.includes(JPEG_RESPONSE_CONTENT_TYPE_PARAM)) {
    return MIME_TYPES.IMAGE_JPEG;
  }
  if (url.includes(PNG_RESPONSE_CONTENT_TYPE_PARAM)) {
    return MIME_TYPES.IMAGE_PNG;
  }
  return MIME_TYPES.OCTET_STREAM;
}

/**
 * Checks whether source URL is defined and not the placeholder marker or the
 * static error-image sentinel (a real path, so it would otherwise pass as
 * "usable" and get rendered as if it were the real preview).
 */
export function isUsableSource(
  url: string | undefined
): url is string {
  return Boolean(
    url && url !== PLACEHOLDER_SOURCE && url !== DOCUMENT_ERROR_SOURCE
  );
}

/**
 * Checks whether source URL is persisted (non-placeholder and non-blob).
 */
export function isPersistedSource(
  url: string | undefined
): url is string {
  return isUsableSource(url) && !isBlobUrl(url);
}

/**
 * Checks whether source URL can be rendered directly and is not a PDF signed source.
 */
export function isDisplayableNonPdfSource(
  url: string | undefined
): url is string {
  return isUsableSource(url) && !isPdfSignedSource(url);
}

function hasImageResponseContentType(url: string): boolean {
  const lower = url.toLowerCase();
  return (
    lower.includes(JPEG_RESPONSE_CONTENT_TYPE_PARAM.toLowerCase()) ||
    lower.includes(PNG_RESPONSE_CONTENT_TYPE_PARAM.toLowerCase()) ||
    lower.includes('response-content-type=image%2f')
  );
}

/**
 * True when a URL can be rendered in an {@code img} tag (not a raw PDF blob/URL).
 * Requires an explicit image MIME param or image file extension in the URL.
 */
export function isImagePreviewUrl(url: string | undefined): url is string {
  if (!isDisplayableNonPdfSource(url)) {
    return false;
  }

  return (
    hasImageResponseContentType(url) || IMAGE_EXTENSION_PATTERN.test(url)
  );
}
